import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';

interface ReplyConfigProps {
    node: FlowNode;
    onClose: () => void;
}

export default function ReplyConfig({ node, onClose }: ReplyConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [message, setMessage] = useState(node.data.config.message || '');

    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: async () => {
            toast({ title: 'Salvo!', description: 'Resposta pública atualizada.' });
            await queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleSave = () => {
        if (!message.trim()) {
            toast({
                variant: 'destructive',
                title: 'Mensagem obrigatória',
                description: 'A resposta pública precisa de um texto.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            config: { message },
        });
    };

    const isSaving = updateMutation.isPending;

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Resposta ao Comentário"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    <div>
                        <Label htmlFor="reply-message" className="flex items-center gap-1.5">
                            <MessageCircle className="w-4 h-4 text-green-500" />
                            Mensagem de Resposta Pública *
                        </Label>
                        <Textarea
                            id="reply-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Ex: Obrigado pelo seu comentário! Enviamos um direct pra você"
                            rows={4}
                            className="mt-1.5 resize-none"
                            disabled={isSaving}
                        />
                        <div className="text-xs text-gray-400 mt-1.5 space-y-1">
                            <p>Esta mensagem será postada como resposta pública ao comentário do lead.</p>
                            <p>
                                Variáveis:
                                {' '}
                                <code className="bg-gray-100 px-1 rounded">{'{{$first_name}}'}</code>
                                {' '}
                                <code className="bg-gray-100 px-1 rounded">{'{{$last_name}}'}</code>
                                {' '}
                                <code className="bg-gray-100 px-1 rounded">{'{{$full_name}}'}</code>
                            </p>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs text-green-700">
                            Esta resposta será visível publicamente como comentário da página no post.
                        </p>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !message.trim()}>
                    {isSaving ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar
                        </>
                    )}
                </Button>
            </div>
        </>
    );
}
